import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  Check,
  Clock,
  Home,
  KeyRound,
  Palmtree,
  Pencil,
  Trash2,
  User,
  UserPlus,
  X
} from 'lucide-react';
import PVSPageLayout from './PVSPageLayout';
import {
  getCurrentUser,
  getCurrentUserDisplayName,
  getDisplayNameForUser
} from '../utils/currentUser';
import {
  DEFAULT_NATION,
  NATION_LABELS,
  NATIONS,
  isWorkingDay,
  workingDayCount
} from '../utils/ukBankHolidays';

const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:3001'
  : 'https://app.pvslimited.co.uk';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const JOB_TITLES = [
  'Director',
  'Driver',
  'Coatings Inspector',
  'Vessel Inspector',
  'Fabricator'
];

const ACCESS_TYPES = [
  {
    value: 'standard',
    label: 'Standard',
    summary: 'Normal shop-floor access. Can add vessels and complete forms. Cannot delete inventory or manage users.'
  },
  {
    value: 'restricted',
    label: 'Restricted',
    summary: 'Can add new records only. Cannot edit or delete existing inventory.'
  },
  {
    value: 'readonly',
    label: 'Readonly',
    summary: 'View only. Can see inventory and Vessel Mind, but cannot change records.'
  },
  {
    value: 'admin',
    label: 'Admin',
    summary: 'Full access. Can delete inventory, approve leave, set entitlements, and manage users.'
  }
];

const accessLabel = (user) => {
  const value = user?.access || (user?.isAdmin ? 'admin' : user?.role) || 'standard';
  return ACCESS_TYPES.find((item) => item.value === value)?.label || value;
};

const nameWithJobTitle = (user) => {
  const name = getDisplayNameForUser(user);
  return user?.jobTitle ? `${name} · ${user.jobTitle}` : name;
};

const DAY_TYPE_LABELS = {
  full: 'Full day',
  am: 'Morning',
  pm: 'Afternoon'
};

const STATUS_CLASS = {
  pending: 'text-amber-300 bg-amber-400/10 border-amber-400/30',
  approved: 'text-pvs-accent bg-pvs-accent/10 border-pvs-accent/30',
  rejected: 'text-red-300 bg-red-400/10 border-red-400/30',
  cancelled: 'text-pvs-text-muted bg-white/5 border-white/10'
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

const formatDays = (value) => {
  const n = Number(value) || 0;
  const shown = n % 1 === 0 ? String(n) : n.toFixed(1);
  return `${shown} ${n === 1 ? 'day' : 'days'}`;
};

const formatDate = (key) => {
  if (!key) return '';
  const [year, month, day] = key.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

const pad2 = (value) => String(value).padStart(2, '0');

const londonDateKey = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date());

const addDaysKey = (key, days) => {
  const [year, month, day] = key.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
};

const mondayOf = (key) => {
  const [year, month, day] = String(key || londonDateKey()).split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const weekday = date.getUTCDay();
  date.setUTCDate(date.getUTCDate() + (weekday === 0 ? -6 : 1 - weekday));
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
};

const TIMESHEET_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DEFAULT_CONTRACTED_HOURS = 39;

const defaultTimesForDay = (dayName) => {
  if (dayName === 'Friday') return { start: '08:00', end: '15:00' };
  if (dayName === 'Monday' || dayName === 'Tuesday' || dayName === 'Wednesday' || dayName === 'Thursday') {
    return { start: '08:00', end: '16:00' };
  }
  return { start: '', end: '' };
};

const defaultWeekTimes = (weekStart) => {
  const next = {};
  TIMESHEET_DAYS.forEach((dayName, index) => {
    next[addDaysKey(weekStart, index)] = defaultTimesForDay(dayName);
  });
  return next;
};

const hoursBetween = (start, end) => {
  if (!start || !end) return 0;
  const [startHour, startMinute] = start.split(':').map(Number);
  const [endHour, endMinute] = end.split(':').map(Number);
  const minutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
  if (!Number.isFinite(minutes) || minutes <= 0) return 0;
  return Math.round((minutes / 60) * 100) / 100;
};

const formatHours = (value) => {
  const hours = Math.round((Number(value) || 0) * 100) / 100;
  const shown = hours % 1 === 0 ? String(hours) : hours.toFixed(2);
  return `${shown} ${hours === 1 ? 'hour' : 'hours'}`;
};

const formatRange = (request) => {
  if (!request) return '';
  if (request.startDate === request.endDate) {
    const half = request.dayType !== 'full' ? ` (${DAY_TYPE_LABELS[request.dayType]})` : '';
    return `${formatDate(request.startDate)}${half}`;
  }
  return `${formatDate(request.startDate)} – ${formatDate(request.endDate)}`;
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${STATUS_CLASS[status] || STATUS_CLASS.pending}`}>
    {status}
  </span>
);

const HOLIDAY_ADMIN_EMAILS = ['gareth@pvslimited.com', 'contact@pvslimited.com', 'contact@pvslimited'];
const HOLIDAY_ADMIN_USERS = ['gareth', 'gareth_holton', 'contact', 'office'];

const isHolidayAdminUser = (user) => {
  const email = String(user?.email || '').toLowerCase().trim();
  const username = String(user?.username || '').toLowerCase().trim();
  const role = user?.role;
  return user?.isAdmin === true
    || HOLIDAY_ADMIN_EMAILS.includes(email)
    || HOLIDAY_ADMIN_USERS.includes(username)
    || role === 'admin'
    || role === 'system_admin';
};

const requestedWorkingDays = (start, end, type, nation) => {
  if (!start) return 0;
  const finish = end || start;
  if (start === finish && (type === 'am' || type === 'pm')) {
    return isWorkingDay(start, nation) ? 0.5 : 0;
  }
  return workingDayCount(start, finish, nation);
};

const staffMemberId = (member) => String(member?.id || member?._id || '');

const Profile = ({ onReturnHome }) => {
  const currentUser = getCurrentUser();
  const isAdmin = isHolidayAdminUser(currentUser);
  const displayName = getCurrentUserDisplayName() || currentUser?.username || 'Profile';

  const [tab, setTab] = useState('leave');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [profile, setProfile] = useState(null);
  const [balance, setBalance] = useState(null);
  const [upcomingLeave, setUpcomingLeave] = useState([]);
  const [requests, setRequests] = useState([]);
  const [adminRequests, setAdminRequests] = useState([]);
  const [staff, setStaff] = useState([]);
  const [managedUsers, setManagedUsers] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dayType, setDayType] = useState('full');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [entitlementScope, setEntitlementScope] = useState('all');
  const [entitlementUserId, setEntitlementUserId] = useState('');
  const [entitlementDays, setEntitlementDays] = useState(21);
  const [resetScope, setResetScope] = useState('all');
  const [resetUserId, setResetUserId] = useState('');
  const [resetMonth, setResetMonth] = useState(1);
  const [resetDay, setResetDay] = useState(1);
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newJobTitle, setNewJobTitle] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newAccess, setNewAccess] = useState('standard');
  const [savingUser, setSavingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState('');
  const [editFullName, setEditFullName] = useState('');
  const [editJobTitle, setEditJobTitle] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editAccess, setEditAccess] = useState('standard');
  const [resetPassword, setResetPassword] = useState('');
  const [weekCommencing, setWeekCommencing] = useState(() => mondayOf(londonDateKey()));
  const [dayTimes, setDayTimes] = useState(() => defaultWeekTimes(mondayOf(londonDateKey())));
  const [submittingTimesheet, setSubmittingTimesheet] = useState(false);
  const [contractedScope, setContractedScope] = useState('all');
  const [contractedUserId, setContractedUserId] = useState('');
  const [contractedHoursInput, setContractedHoursInput] = useState(DEFAULT_CONTRACTED_HOURS);
  const [newContractedHours, setNewContractedHours] = useState(DEFAULT_CONTRACTED_HOURS);
  const [editContractedHours, setEditContractedHours] = useState(DEFAULT_CONTRACTED_HOURS);
  const [newHolidayNation, setNewHolidayNation] = useState(DEFAULT_NATION);
  const [editHolidayNation, setEditHolidayNation] = useState(DEFAULT_NATION);
  const [nationScope, setNationScope] = useState('all');
  const [nationUserId, setNationUserId] = useState('');
  const [holidayNationInput, setHolidayNationInput] = useState(DEFAULT_NATION);

  const singleDay = Boolean(startDate) && startDate === (endDate || startDate);

  const loadMe = useCallback(async () => {
    const response = await fetch(`${API_BASE}/api/holiday/me`, { headers: authHeaders() });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Unable to load profile');
    }
    setProfile(data.profile);
    setBalance(data.balance);
    setUpcomingLeave(data.upcomingLeave || []);
    setRequests(data.requests || []);
  }, []);

  const loadAdmin = useCallback(async () => {
    if (!isAdmin) return;
    const [requestsRes, staffRes, usersRes] = await Promise.all([
      fetch(`${API_BASE}/api/holiday/admin/requests?status=all`, { headers: authHeaders() }),
      fetch(`${API_BASE}/api/holiday/admin/staff`, { headers: authHeaders() }),
      fetch(`${API_BASE}/api/holiday/admin/users`, { headers: authHeaders() })
    ]);
    const requestsData = await requestsRes.json().catch(() => ({}));
    const staffData = await staffRes.json().catch(() => ({}));
    const usersData = await usersRes.json().catch(() => ({}));
    if (!requestsRes.ok || !requestsData.success) {
      throw new Error(requestsData.error || 'Unable to load leave requests');
    }
    if (!staffRes.ok || !staffData.success) {
      throw new Error(staffData.error || 'Unable to load staff');
    }
    if (!usersRes.ok || !usersData.success) {
      throw new Error(usersData.error || 'Unable to load users');
    }
    setAdminRequests(requestsData.requests || []);
    setStaff(Array.isArray(staffData.staff) ? staffData.staff : []);
    setManagedUsers(Array.isArray(usersData.users) ? usersData.users : []);
    setCompanySettings(staffData.settings || null);
    if (staffData.settings) {
      setEntitlementDays(staffData.settings.entitlementDays);
      setResetMonth(staffData.settings.resetMonth);
      setResetDay(staffData.settings.resetDay);
      if (staffData.settings.holidayNation) {
        setHolidayNationInput(staffData.settings.holidayNation);
      }
    }
  }, [isAdmin]);

  const refresh = useCallback(async () => {
    await Promise.all([loadMe(), loadAdmin()]);
  }, [loadMe, loadAdmin]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadMe();
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
      try {
        await loadAdmin();
      } catch (err) {
        if (!cancelled) setError((current) => current || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadMe, loadAdmin]);

  useEffect(() => {
    if (!isAdmin && tab !== 'leave') {
      setTab('leave');
    }
  }, [isAdmin, tab]);

  useEffect(() => {
    if (startDate && endDate && startDate !== endDate) setDayType('full');
  }, [startDate, endDate]);

  useEffect(() => {
    setDayTimes(defaultWeekTimes(weekCommencing));
  }, [weekCommencing]);

  const pendingAdminRequests = useMemo(
    () => adminRequests.filter((item) => item.status === 'pending'),
    [adminRequests]
  );

  const todayKey = londonDateKey();
  const requestedDays = requestedWorkingDays(startDate, endDate, singleDay ? dayType : 'full', profile?.holidayNation || DEFAULT_NATION);
  const availableDays = Math.round((((balance?.remainingDays || 0) - (balance?.pendingDays || 0)) * 2)) / 2;
  const weekendOnly = Boolean(startDate && (endDate || startDate) && requestedDays === 0);
  const insufficientLeave = requestedDays > 0 && requestedDays > availableDays;

  const staffLabel = (member) => {
    const name = nameWithJobTitle(member);
    const remaining = member.balance?.remainingDays;
    return remaining == null ? name : `${name} — ${formatDays(remaining)} left`;
  };

  const timesheetRows = useMemo(
    () => TIMESHEET_DAYS.map((dayName, index) => ({
      date: addDaysKey(weekCommencing, index),
      dayName
    })),
    [weekCommencing]
  );

  const timesheetTotals = useMemo(() => {
    const daily = timesheetRows.map((row) => {
      const times = dayTimes[row.date] || {};
      return hoursBetween(times.start, times.end);
    });
    const totalHours = Math.round(daily.reduce((sum, hours) => sum + hours, 0) * 100) / 100;
    const contractedHours = Number(profile?.contractedHours ?? DEFAULT_CONTRACTED_HOURS);
    const overtimeHours = Math.round(Math.max(0, totalHours - contractedHours) * 100) / 100;
    return { totalHours, contractedHours, overtimeHours };
  }, [timesheetRows, dayTimes, profile]);

  const flash = (text) => {
    setMessage(text);
    window.setTimeout(() => setMessage(''), 3500);
  };

  const submitRequest = async (event) => {
    event.preventDefault();
    setError('');
    if (weekendOnly) {
      setError('This request has no working days. Weekends and public holidays are not counted.');
      return;
    }
    if (insufficientLeave) {
      setError(`Not enough remaining holiday. You asked for ${formatDays(requestedDays)}, but you only have ${formatDays(availableDays)} left. Weekends and public holidays are not deducted.`);
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/holiday/requests`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          startDate,
          endDate: endDate || startDate,
          dayType: singleDay ? dayType : 'full',
          notes
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to submit leave request');
      }
      setStartDate('');
      setEndDate('');
      setDayType('full');
      setNotes('');
      flash('Leave request submitted');
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const currentAdminRequests = adminRequests.filter((item) => item.endDate >= todayKey);
  const currentRequests = requests.filter((item) => item.endDate >= todayKey);

  const takenLeaveByUser = useMemo(() => {
    const byUser = new Map();
    adminRequests
      .filter((item) => item.status === 'approved' && item.endDate < todayKey)
      .sort((a, b) => (a.startDate < b.startDate ? 1 : -1))
      .forEach((item) => {
        const key = String(item.userId);
        const entry = byUser.get(key) || { totalDays: 0, items: [] };
        entry.totalDays += Number(item.days) || 0;
        entry.items.push(item);
        byUser.set(key, entry);
      });
    return byUser;
  }, [adminRequests, todayKey]);

  const canCancelRequest = (item) => (
    item.status === 'pending'
    || (item.status === 'approved' && (isAdmin ? item.endDate >= todayKey : item.startDate > todayKey))
  );

  const cancelRequest = async (id, wasApproved = false) => {
    if (wasApproved && !window.confirm('Cancel this approved leave? It will be removed from the schedule and the days returned.')) return;
    setError('');
    try {
      const url = isAdmin
        ? `${API_BASE}/api/holiday/admin/requests/${id}/cancel`
        : `${API_BASE}/api/holiday/requests/${id}/cancel`;
      const response = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to cancel request');
      }
      flash('Request cancelled');
      await refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const reviewRequest = async (id, action) => {
    if (action === 'cancel' && !window.confirm('Cancel this approved leave? It will be removed from the schedule and the days returned.')) return;
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/holiday/admin/requests/${id}/${action}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Unable to ${action} request`);
      }
      const flashText = { approve: 'Leave approved', reject: 'Leave rejected', cancel: 'Leave cancelled' };
      flash(flashText[action]);
      await refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const saveEntitlement = async (event) => {
    event.preventDefault();
    setSavingAdmin(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/holiday/admin/entitlement`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          all: entitlementScope === 'all',
          userId: entitlementScope === 'one' ? entitlementUserId : undefined,
          days: Number(entitlementDays)
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to update entitlement');
      }
      flash(entitlementScope === 'all' ? 'Entitlement updated for all staff' : 'Entitlement updated');
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingAdmin(false);
    }
  };

  const saveResetDate = async (event) => {
    event.preventDefault();
    setSavingAdmin(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/holiday/admin/reset-date`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          all: resetScope === 'all',
          userId: resetScope === 'one' ? resetUserId : undefined,
          month: Number(resetMonth),
          day: Number(resetDay)
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to update reset date');
      }
      flash(resetScope === 'all' ? 'Reset date updated for all staff' : 'Reset date updated');
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingAdmin(false);
    }
  };

  const saveContractedHours = async (event) => {
    event.preventDefault();
    setSavingAdmin(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/holiday/admin/contracted-hours`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          all: contractedScope === 'all',
          userId: contractedScope === 'one' ? contractedUserId : undefined,
          hours: Number(contractedHoursInput)
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to update contracted hours');
      }
      flash(contractedScope === 'all' ? 'Contracted hours updated for all staff' : 'Contracted hours updated');
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingAdmin(false);
    }
  };

  const saveHolidayNation = async (event) => {
    event.preventDefault();
    setSavingAdmin(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/holiday/admin/holiday-nation`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          all: nationScope === 'all',
          userId: nationScope === 'one' ? nationUserId : undefined,
          nation: holidayNationInput
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to update holiday location');
      }
      flash(nationScope === 'all' ? 'Holiday location updated for all staff' : 'Holiday location updated');
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingAdmin(false);
    }
  };

  const setDayTime = (date, field, value) => {
    setDayTimes((current) => ({
      ...current,
      [date]: { start: '', end: '', ...(current[date] || {}), [field]: value }
    }));
  };

  const submitTimesheet = async (event) => {
    event.preventDefault();
    setError('');
    if (timesheetTotals.totalHours <= 0) {
      setError('Enter at least one day’s start and end time');
      return;
    }
    setSubmittingTimesheet(true);
    try {
      const response = await fetch(`${API_BASE}/api/timesheet/submit`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          weekCommencing,
          days: timesheetRows.map((row) => ({
            date: row.date,
            startTime: dayTimes[row.date]?.start || '',
            endTime: dayTimes[row.date]?.end || ''
          }))
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to submit timesheet');
      }
      setDayTimes(defaultWeekTimes(weekCommencing));
      flash('Timesheet emailed to forms@pvslimited.com');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmittingTimesheet(false);
    }
  };

  const addUser = async (event) => {
    event.preventDefault();
    setSavingUser(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/holiday/admin/users`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          username: newUsername,
          fullName: newFullName,
          jobTitle: newJobTitle,
          password: newPassword,
          email: newEmail,
          access: newAccess,
          contractedHours: Number(newContractedHours),
          holidayNation: newHolidayNation
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to add user');
      }
      setNewUsername('');
      setNewFullName('');
      setNewJobTitle('');
      setNewPassword('');
      setNewEmail('');
      setNewAccess('standard');
      setNewContractedHours(DEFAULT_CONTRACTED_HOURS);
      setNewHolidayNation(DEFAULT_NATION);
      flash('User added');
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingUser(false);
    }
  };

  const removeUser = async (user) => {
    const name = getDisplayNameForUser(user);
    if (!window.confirm(`Remove ${name}? They will no longer be able to sign in.`)) return;
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/holiday/admin/users/${user.id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to remove user');
      }
      flash('User removed');
      await refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const startEditUser = (user) => {
    setEditingUserId(user.id);
    setEditFullName(user.fullName || getDisplayNameForUser(user));
    setEditJobTitle(user.jobTitle || '');
    setEditUsername(user.username || '');
    setEditEmail(user.email || '');
    setEditAccess(user.access || (user.isAdmin ? 'admin' : user.role) || 'standard');
    setEditContractedHours(user.contractedHours ?? DEFAULT_CONTRACTED_HOURS);
    setEditHolidayNation(user.holidayNation || DEFAULT_NATION);
    setResetPassword('');
  };

  const saveEditUser = async (event) => {
    event.preventDefault();
    setSavingUser(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/holiday/admin/users/${editingUserId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          username: editUsername,
          fullName: editFullName,
          jobTitle: editJobTitle,
          email: editEmail,
          access: editAccess,
          contractedHours: Number(editContractedHours),
          holidayNation: editHolidayNation
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to update user');
      }
      setEditingUserId('');
      setResetPassword('');
      flash('User updated');
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingUser(false);
    }
  };

  const resetUserPassword = async (userId) => {
    setError('');
    if (resetPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setSavingUser(true);
    try {
      const response = await fetch(`${API_BASE}/api/holiday/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ password: resetPassword })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to reset password');
      }
      setResetPassword('');
      flash('Password reset');
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingUser(false);
    }
  };

  const roleLabel = profile?.isAdmin ? 'Admin' : (profile?.role || currentUser?.role || 'standard');

  if (loading) {
    return (
      <PVSPageLayout className="py-6 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pvs-accent mx-auto" />
          <p className="text-pvs-text-muted mt-4">Loading profile...</p>
        </div>
      </PVSPageLayout>
    );
  }

  return (
    <PVSPageLayout className="py-2 sm:py-6 px-2 sm:px-4">
      <div className="max-w-3xl mx-auto pb-10">
        <div className="pvs-glass mb-4 sm:mb-6 p-4 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="p-3 rounded-2xl flex-shrink-0"
                style={{
                  background: 'rgba(74, 222, 128, 0.06)',
                  border: '1px solid rgba(74, 222, 128, 0.15)'
                }}
              >
                <User className="h-7 w-7" stroke="url(#icon-two-tone)" strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-3xl font-bold text-white truncate">
                  {displayName}
                  {profile?.jobTitle ? ` · ${profile.jobTitle}` : ''}
                </h1>
                <p className="text-sm text-pvs-text-muted mt-1">
                  @{profile?.username || currentUser?.username} · {roleLabel}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onReturnHome}
              className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 pvs-glass pvs-glass-hover"
              title="Return to Homepage"
            >
              <Home className="w-5 h-5 sm:w-6 sm:h-6" stroke="url(#icon-two-tone)" />
            </button>
          </div>

          {isAdmin && (
            <div className="flex flex-wrap gap-2 mt-4">
              {[
                { id: 'leave', label: 'My leave' },
                { id: 'approvals', label: `Approvals${pendingAdminRequests.length ? ` (${pendingAdminRequests.length})` : ''}` },
                { id: 'staff', label: 'Entitlements' },
                { id: 'users', label: 'Manage users' }
              ].map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setTab(item.id)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium ${
                    tab === item.id
                      ? 'bg-pvs-accent text-pvs-bg'
                      : 'pvs-glass pvs-glass-hover text-pvs-text-muted'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl border border-red-400/30 bg-red-400/10 text-red-200 text-sm">{error}</div>
        )}
        {message && (
          <div className="mb-4 p-3 rounded-xl border border-pvs-accent/30 bg-pvs-accent/10 text-pvs-accent text-sm">{message}</div>
        )}

        {tab === 'leave' && (
          <>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div className="pvs-glass p-5">
                <div className="flex items-center gap-2 text-pvs-text-muted text-sm mb-2">
                  <Palmtree className="w-4 h-4" /> Remaining
                </div>
                <p className="text-4xl font-bold text-white">{balance ? formatDays(balance.remainingDays) : '—'}</p>
                <p className="text-sm text-pvs-text-muted mt-2">
                  of {balance ? formatDays(balance.entitlementDays) : '21 days'}
                </p>
              </div>
              <div className="pvs-glass p-5">
                <div className="flex items-center gap-2 text-pvs-text-muted text-sm mb-2">
                  <Clock className="w-4 h-4" /> Used this year
                </div>
                <p className="text-2xl font-semibold text-white">{balance ? formatDays(balance.usedDays) : '—'}</p>
                {balance?.pendingDays > 0 && (
                  <p className="text-sm text-amber-300 mt-2">{formatDays(balance.pendingDays)} pending</p>
                )}
              </div>
              <div className="pvs-glass p-5">
                <div className="flex items-center gap-2 text-pvs-text-muted text-sm mb-2">
                  <CalendarDays className="w-4 h-4" /> Refreshes
                </div>
                <p className="text-xl font-semibold text-white">{balance?.nextResetLabel || '—'}</p>
                <p className="text-sm text-pvs-text-muted mt-2">Holiday year started {balance?.yearStartLabel}</p>
              </div>
            </div>

            <form onSubmit={submitRequest} className="pvs-glass p-4 sm:p-6 mb-4">
              <h2 className="text-lg font-semibold text-pvs-accent mb-4">Request leave</h2>
              <p className="text-sm text-pvs-text-muted mb-4">
                End date is the last day you are off, not the day you return to work. Saturdays, Sundays and public holidays for {NATION_LABELS[profile?.holidayNation] || 'England'} are not deducted.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <label className="block">
                  <span className="text-sm text-pvs-text-muted mb-1 block">Start date</span>
                  <input
                    type="date"
                    required
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      if (!endDate || endDate < e.target.value) setEndDate(e.target.value);
                    }}
                    className="pvs-input w-full"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-pvs-text-muted mb-1 block">End date</span>
                  <input
                    type="date"
                    required
                    value={endDate}
                    min={startDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="pvs-input w-full"
                  />
                </label>
              </div>

              {singleDay && (
                <div className="flex gap-2 mb-3">
                  {['full', 'am', 'pm'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setDayType(type)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                        dayType === type
                          ? 'bg-pvs-accent text-pvs-bg'
                          : 'pvs-glass pvs-glass-hover text-pvs-text-muted'
                      }`}
                    >
                      {DAY_TYPE_LABELS[type]}
                    </button>
                  ))}
                </div>
              )}

              {!singleDay && startDate && endDate && (
                <p className="text-sm text-pvs-text-muted mb-3">Date ranges are booked as full working days. Weekends and public holidays are not counted.</p>
              )}

              {startDate && (
                <div className={`mb-3 p-3 rounded-xl border text-sm ${
                  insufficientLeave || weekendOnly
                    ? 'border-red-400/40 bg-red-400/10 text-red-200'
                    : 'border-pvs-accent/30 bg-pvs-accent/10 text-pvs-accent'
                }`}>
                  {weekendOnly && 'This range has no working days. Weekends and public holidays are not counted.'}
                  {insufficientLeave && `Not enough remaining holiday. You asked for ${formatDays(requestedDays)}, but you only have ${formatDays(availableDays)} left. Weekends and public holidays are not deducted.`}
                  {!weekendOnly && !insufficientLeave && `This request is ${formatDays(requestedDays)} (weekends and public holidays not counted). You will have ${formatDays(availableDays - requestedDays)} left.`}
                </div>
              )}

              <label className="block mb-4">
                <span className="text-sm text-pvs-text-muted mb-1 block">Notes (optional)</span>
                <input
                  type="text"
                  maxLength={500}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="pvs-input w-full"
                  placeholder="Reason or extra detail"
                />
              </label>

              <button type="submit" disabled={submitting || insufficientLeave || weekendOnly} className="pvs-btn-accent px-4 py-2 disabled:opacity-50">
                {submitting ? 'Submitting...' : 'Submit request'}
              </button>
            </form>

            <form onSubmit={submitTimesheet} className="pvs-glass p-4 sm:p-6 mb-4">
              <h2 className="text-lg font-semibold text-pvs-accent mb-1">Timesheet</h2>
              <p className="text-sm text-pvs-text-muted mb-4">
                Standard hours are pre-filled: Monday to Thursday 08:00–16:00, Friday 08:00–15:00. Overtime is hours above your contracted {formatHours(timesheetTotals.contractedHours)}.
              </p>
              <label className="block mb-4">
                <span className="text-sm text-pvs-text-muted mb-1 block">Week commencing (Monday)</span>
                <input
                  type="date"
                  required
                  value={weekCommencing}
                  onChange={(e) => setWeekCommencing(mondayOf(e.target.value))}
                  className="pvs-input w-full sm:w-auto"
                />
              </label>
              <div className="space-y-3 mb-4">
                {timesheetRows.map((row) => {
                  const times = dayTimes[row.date] || {};
                  const hours = hoursBetween(times.start, times.end);
                  return (
                    <div key={row.date} className="border border-white/10 rounded-xl px-3 py-3">
                      <div className="flex items-baseline justify-between gap-3 mb-2">
                        <p className="text-white font-medium">{row.dayName}</p>
                        <p className="text-sm text-pvs-text-muted">{formatDate(row.date)}</p>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <label className="block">
                          <span className="text-xs text-pvs-text-muted mb-1 block">Start</span>
                          <input
                            type="time"
                            value={times.start || ''}
                            onChange={(e) => setDayTime(row.date, 'start', e.target.value)}
                            className="pvs-input w-full"
                          />
                        </label>
                        <label className="block">
                          <span className="text-xs text-pvs-text-muted mb-1 block">End</span>
                          <input
                            type="time"
                            value={times.end || ''}
                            onChange={(e) => setDayTime(row.date, 'end', e.target.value)}
                            className="pvs-input w-full"
                          />
                        </label>
                        <p className="col-span-2 sm:col-span-1 text-sm text-pvs-accent self-end pb-2">
                          {formatHours(hours)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="grid sm:grid-cols-3 gap-3 mb-4">
                <div className="p-3 rounded-xl border border-white/10">
                  <p className="text-xs text-pvs-text-muted">Total hours</p>
                  <p className="text-white font-semibold">{formatHours(timesheetTotals.totalHours)}</p>
                </div>
                <div className="p-3 rounded-xl border border-white/10">
                  <p className="text-xs text-pvs-text-muted">Contracted</p>
                  <p className="text-white font-semibold">{formatHours(timesheetTotals.contractedHours)}</p>
                </div>
                <div className="p-3 rounded-xl border border-pvs-accent/30 bg-pvs-accent/10">
                  <p className="text-xs text-pvs-text-muted">Overtime</p>
                  <p className="text-pvs-accent font-semibold">{formatHours(timesheetTotals.overtimeHours)}</p>
                </div>
              </div>
              <button type="submit" disabled={submittingTimesheet || timesheetTotals.totalHours <= 0} className="pvs-btn-accent px-4 py-2 disabled:opacity-50">
                {submittingTimesheet ? 'Sending...' : 'Submit timesheet'}
              </button>
            </form>

            <div className="pvs-glass p-4 sm:p-6 mb-4">
              <h2 className="text-lg font-semibold text-pvs-accent mb-4">Upcoming leave</h2>
              {upcomingLeave.length === 0 ? (
                <p className="text-sm text-pvs-text-muted">No approved leave coming up.</p>
              ) : (
                <div className="space-y-3">
                  {upcomingLeave.map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-3 border border-pvs-accent/20 rounded-xl px-3 py-3">
                      <div>
                        <p className="text-white font-medium">{formatRange(item)}</p>
                        <p className="text-sm text-pvs-text-muted">{formatDays(item.days)}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <StatusBadge status={item.status} />
                        {canCancelRequest(item) && (
                          <button
                            type="button"
                            onClick={() => cancelRequest(item.id, true)}
                            className="px-2 py-1 text-xs rounded-lg border border-white/15 text-pvs-text-muted hover:text-white"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pvs-glass p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-pvs-accent mb-4">My requests</h2>
              {currentRequests.length === 0 ? (
                <p className="text-sm text-pvs-text-muted">No current or upcoming leave requests.</p>
              ) : (
                <div className="space-y-3">
                  {[...currentRequests].reverse().map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-3 border border-white/10 rounded-xl px-3 py-3">
                      <div className="min-w-0">
                        <p className="text-white font-medium">{formatRange(item)}</p>
                        <p className="text-sm text-pvs-text-muted">
                          {formatDays(item.days)}
                          {item.notes ? ` · ${item.notes}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <StatusBadge status={item.status} />
                        {canCancelRequest(item) && (
                          <button
                            type="button"
                            onClick={() => cancelRequest(item.id, item.status === 'approved')}
                            className="px-2 py-1 text-xs rounded-lg border border-white/15 text-pvs-text-muted hover:text-white"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {tab === 'approvals' && isAdmin && (
          <div className="pvs-glass p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-pvs-accent mb-4">Holiday requests</h2>
            {currentAdminRequests.length === 0 ? (
              <p className="text-sm text-pvs-text-muted">No current or upcoming leave requests.</p>
            ) : (
              <div className="space-y-3">
                {currentAdminRequests.map((item) => (
                  <div key={item.id} className="border border-white/10 rounded-xl px-3 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-white font-medium">
                          {nameWithJobTitle(staff.find((member) => member.username === item.username) || { username: item.username })}
                        </p>
                        <p className="text-sm text-pvs-text-muted">{formatRange(item)} · {formatDays(item.days)}</p>
                        {item.notes ? <p className="text-sm text-pvs-text-muted mt-1">{item.notes}</p> : null}
                        {staff.find((member) => member.username === item.username)?.balance && (
                          <p className="text-sm text-pvs-accent mt-1">
                            {formatDays(staff.find((member) => member.username === item.username).balance.remainingDays)} remaining
                          </p>
                        )}
                      </div>
                      <StatusBadge status={item.status} />
                    </div>
                    {item.status === 'pending' && (
                      <div className="flex gap-2 mt-3">
                        <button
                          type="button"
                          onClick={() => reviewRequest(item.id, 'approve')}
                          className="flex items-center gap-1 px-3 py-2 rounded-lg bg-pvs-accent text-pvs-bg text-sm font-semibold"
                        >
                          <Check className="w-4 h-4" /> Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => reviewRequest(item.id, 'reject')}
                          className="flex items-center gap-1 px-3 py-2 rounded-lg border border-red-400/40 text-red-300 text-sm font-semibold"
                        >
                          <X className="w-4 h-4" /> Reject
                        </button>
                      </div>
                    )}
                    {item.status === 'approved' && item.endDate >= todayKey && (
                      <div className="flex gap-2 mt-3">
                        <button
                          type="button"
                          onClick={() => reviewRequest(item.id, 'cancel')}
                          className="flex items-center gap-1 px-3 py-2 rounded-lg border border-red-400/40 text-red-300 text-sm font-semibold"
                        >
                          <X className="w-4 h-4" /> Cancel leave
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'staff' && isAdmin && (
          <div className="space-y-4">
            <div className="pvs-glass p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-pvs-accent mb-4">Days remaining</h2>
              {staff.length === 0 ? (
                <p className="text-sm text-pvs-text-muted">No employees loaded. If this stays empty, refresh or check you are signed in as admin.</p>
              ) : (
                <div className="space-y-2">
                  {staff.map((member) => (
                    <div key={staffMemberId(member)} className="flex items-center justify-between gap-3 border border-white/10 rounded-xl px-3 py-3">
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{nameWithJobTitle(member)}</p>
                        <p className="text-sm text-pvs-text-muted">
                          {formatDays(member.balance?.entitlementDays)} entitlement · refreshes {member.balance?.nextResetLabel}
                          {member.contractedHours != null ? ` · ${formatHours(member.contractedHours)} contracted` : ''}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-white font-semibold">{formatDays(member.balance?.remainingDays)}</p>
                        <p className="text-xs text-pvs-text-muted">{formatDays(member.balance?.usedDays)} used</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={saveEntitlement} className="pvs-glass p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-pvs-accent mb-4">Holiday entitlement</h2>
              <p className="text-sm text-pvs-text-muted mb-4">
                Company default is {companySettings ? formatDays(companySettings.entitlementDays) : '21 days'}.
              </p>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setEntitlementScope('all')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                    entitlementScope === 'all' ? 'bg-pvs-accent text-pvs-bg' : 'pvs-glass text-pvs-text-muted'
                  }`}
                >
                  All staff
                </button>
                <button
                  type="button"
                  onClick={() => setEntitlementScope('one')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                    entitlementScope === 'one' ? 'bg-pvs-accent text-pvs-bg' : 'pvs-glass text-pvs-text-muted'
                  }`}
                >
                  One person
                </button>
              </div>
              {entitlementScope === 'one' && (
                <select
                  required
                  value={entitlementUserId}
                  onChange={(e) => setEntitlementUserId(e.target.value)}
                  className="pvs-select w-full mb-3"
                >
                  <option value="">Select staff</option>
                  {staff.map((member) => (
                    <option key={staffMemberId(member)} value={staffMemberId(member)}>
                      {staffLabel(member)}
                    </option>
                  ))}
                </select>
              )}
              <label className="block mb-4">
                <span className="text-sm text-pvs-text-muted mb-1 block">Days</span>
                <input
                  type="number"
                  min="0"
                  max="365"
                  step="0.5"
                  value={entitlementDays}
                  onChange={(e) => setEntitlementDays(e.target.value)}
                  className="pvs-input w-full"
                />
              </label>
              <button type="submit" disabled={savingAdmin} className="pvs-btn-accent px-4 py-2 disabled:opacity-50">
                Save entitlement
              </button>
            </form>

            <form onSubmit={saveResetDate} className="pvs-glass p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-pvs-accent mb-4">Reset date</h2>
              <p className="text-sm text-pvs-text-muted mb-4">
                Remaining holiday refreshes on this date each year.
              </p>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setResetScope('all')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                    resetScope === 'all' ? 'bg-pvs-accent text-pvs-bg' : 'pvs-glass text-pvs-text-muted'
                  }`}
                >
                  All staff
                </button>
                <button
                  type="button"
                  onClick={() => setResetScope('one')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                    resetScope === 'one' ? 'bg-pvs-accent text-pvs-bg' : 'pvs-glass text-pvs-text-muted'
                  }`}
                >
                  One person
                </button>
              </div>
              {resetScope === 'one' && (
                <select
                  required
                  value={resetUserId}
                  onChange={(e) => setResetUserId(e.target.value)}
                  className="pvs-select w-full mb-3"
                >
                  <option value="">Select staff</option>
                  {staff.map((member) => (
                    <option key={staffMemberId(member)} value={staffMemberId(member)}>
                      {staffLabel(member)}
                    </option>
                  ))}
                </select>
              )}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <label className="block">
                  <span className="text-sm text-pvs-text-muted mb-1 block">Day</span>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={resetDay}
                    onChange={(e) => setResetDay(e.target.value)}
                    className="pvs-input w-full"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-pvs-text-muted mb-1 block">Month</span>
                  <select
                    value={resetMonth}
                    onChange={(e) => setResetMonth(e.target.value)}
                    className="pvs-select w-full"
                  >
                    {MONTHS.map((month) => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <button type="submit" disabled={savingAdmin} className="pvs-btn-accent px-4 py-2 disabled:opacity-50">
                Save reset date
              </button>
            </form>

            <form onSubmit={saveContractedHours} className="pvs-glass p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-pvs-accent mb-4">Contracted hours</h2>
              <p className="text-sm text-pvs-text-muted mb-4">
                Weekly contracted hours used to calculate overtime on timesheets.
              </p>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setContractedScope('all')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                    contractedScope === 'all' ? 'bg-pvs-accent text-pvs-bg' : 'pvs-glass text-pvs-text-muted'
                  }`}
                >
                  All staff
                </button>
                <button
                  type="button"
                  onClick={() => setContractedScope('one')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                    contractedScope === 'one' ? 'bg-pvs-accent text-pvs-bg' : 'pvs-glass text-pvs-text-muted'
                  }`}
                >
                  One person
                </button>
              </div>
              {contractedScope === 'one' && (
                <select
                  required
                  value={contractedUserId}
                  onChange={(e) => setContractedUserId(e.target.value)}
                  className="pvs-select w-full mb-3"
                >
                  <option value="">Select staff</option>
                  {staff.map((member) => (
                    <option key={staffMemberId(member)} value={staffMemberId(member)}>
                      {nameWithJobTitle(member)} — {formatHours(member.contractedHours ?? DEFAULT_CONTRACTED_HOURS)}
                    </option>
                  ))}
                </select>
              )}
              <label className="block mb-4">
                <span className="text-sm text-pvs-text-muted mb-1 block">Hours per week</span>
                <input
                  type="number"
                  min="0"
                  max="80"
                  step="0.5"
                  value={contractedHoursInput}
                  onChange={(e) => setContractedHoursInput(e.target.value)}
                  className="pvs-input w-full"
                />
              </label>
              <button type="submit" disabled={savingAdmin} className="pvs-btn-accent px-4 py-2 disabled:opacity-50">
                Save contracted hours
              </button>
            </form>

            <form onSubmit={saveHolidayNation} className="pvs-glass p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-pvs-accent mb-4">Holiday location</h2>
              <p className="text-sm text-pvs-text-muted mb-4">
                Public holidays differ in England, Scotland and Wales. Set the nation used to count leave days.
              </p>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setNationScope('all')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                    nationScope === 'all' ? 'bg-pvs-accent text-pvs-bg' : 'pvs-glass text-pvs-text-muted'
                  }`}
                >
                  All staff
                </button>
                <button
                  type="button"
                  onClick={() => setNationScope('one')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                    nationScope === 'one' ? 'bg-pvs-accent text-pvs-bg' : 'pvs-glass text-pvs-text-muted'
                  }`}
                >
                  One person
                </button>
              </div>
              {nationScope === 'one' && (
                <select
                  required
                  value={nationUserId}
                  onChange={(e) => setNationUserId(e.target.value)}
                  className="pvs-select w-full mb-3"
                >
                  <option value="">Select staff</option>
                  {staff.map((member) => (
                    <option key={staffMemberId(member)} value={staffMemberId(member)}>
                      {nameWithJobTitle(member)} — {NATION_LABELS[member.holidayNation] || 'England'}
                    </option>
                  ))}
                </select>
              )}
              <label className="block mb-4">
                <span className="text-sm text-pvs-text-muted mb-1 block">Nation</span>
                <select
                  value={holidayNationInput}
                  onChange={(e) => setHolidayNationInput(e.target.value)}
                  className="pvs-select w-full"
                >
                  {NATIONS.map((nation) => (
                    <option key={nation} value={nation}>{NATION_LABELS[nation]}</option>
                  ))}
                </select>
              </label>
              <button type="submit" disabled={savingAdmin} className="pvs-btn-accent px-4 py-2 disabled:opacity-50">
                Save holiday location
              </button>
            </form>
          </div>
        )}

        {tab === 'users' && isAdmin && (
          <div className="space-y-4">
            <form onSubmit={addUser} className="pvs-glass p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-pvs-accent mb-4 flex items-center gap-2">
                <UserPlus className="w-5 h-5" /> Add user
              </h2>
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <label className="block">
                  <span className="text-sm text-pvs-text-muted mb-1 block">Name</span>
                  <input
                    type="text"
                    required
                    minLength={2}
                    maxLength={80}
                    value={newFullName}
                    onChange={(e) => setNewFullName(e.target.value)}
                    className="pvs-input w-full"
                    placeholder="e.g. Kevin Walker"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-pvs-text-muted mb-1 block">Job title</span>
                  <select
                    required
                    value={newJobTitle}
                    onChange={(e) => setNewJobTitle(e.target.value)}
                    className="pvs-select w-full"
                  >
                    <option value="">Select job title</option>
                    {JOB_TITLES.map((title) => (
                      <option key={title} value={title}>{title}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm text-pvs-text-muted mb-1 block">Username</span>
                  <input
                    type="text"
                    required
                    minLength={3}
                    maxLength={20}
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    className="pvs-input w-full"
                    placeholder="e.g. kevinwalker"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-pvs-text-muted mb-1 block">Email (optional)</span>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="pvs-input w-full"
                    placeholder="name@pvslimited.com"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-pvs-text-muted mb-1 block">Password</span>
                  <input
                    type="text"
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pvs-input w-full"
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                  />
                </label>
              </div>
              <label className="block mb-3">
                <span className="text-sm text-pvs-text-muted mb-1 block">Account access</span>
                <select
                  value={newAccess}
                  onChange={(e) => setNewAccess(e.target.value)}
                  className="pvs-select w-full"
                >
                  {ACCESS_TYPES.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </label>
              <label className="block mb-3">
                <span className="text-sm text-pvs-text-muted mb-1 block">Contracted hours (weekly)</span>
                <input
                  type="number"
                  min="0"
                  max="80"
                  step="0.5"
                  value={newContractedHours}
                  onChange={(e) => setNewContractedHours(e.target.value)}
                  className="pvs-input w-full"
                />
              </label>
              <label className="block mb-3">
                <span className="text-sm text-pvs-text-muted mb-1 block">Holiday location</span>
                <select
                  value={newHolidayNation}
                  onChange={(e) => setNewHolidayNation(e.target.value)}
                  className="pvs-select w-full"
                >
                  {NATIONS.map((nation) => (
                    <option key={nation} value={nation}>{NATION_LABELS[nation]}</option>
                  ))}
                </select>
              </label>
              <div className="mb-4 p-3 rounded-xl border border-pvs-accent/20 bg-white/5 text-sm text-pvs-text-muted space-y-2">
                {ACCESS_TYPES.map((item) => (
                  <p key={item.value} className={item.value === newAccess ? 'text-white' : ''}>
                    <span className="font-semibold text-pvs-accent">{item.label}:</span> {item.summary}
                  </p>
                ))}
              </div>
              <button type="submit" disabled={savingUser} className="pvs-btn-accent px-4 py-2 disabled:opacity-50">
                {savingUser ? 'Adding...' : 'Add user'}
              </button>
            </form>

            <div className="pvs-glass p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-pvs-accent mb-4">Users</h2>
              {managedUsers.length === 0 ? (
                <p className="text-sm text-pvs-text-muted">No users loaded.</p>
              ) : (
                <div className="space-y-2">
                  {managedUsers.map((user) => {
                    const isSelf = String(user.id) === String(currentUser?.id || profile?.id || '');
                    const editing = editingUserId === user.id;
                    const taken = takenLeaveByUser.get(String(user.id));
                    return (
                      <div key={user.id} className="border border-white/10 rounded-xl px-3 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate">{nameWithJobTitle(user)}</p>
                            <p className="text-sm text-pvs-text-muted truncate">
                              @{user.username}
                              {user.email ? ` · ${user.email}` : ''}
                              {` · ${accessLabel(user)}`}
                            </p>
                            <p className="text-sm text-pvs-accent mt-1">
                              Holiday taken: {formatDays(taken?.totalDays || 0)}
                            </p>
                            {taken && (
                              <ul className="mt-1 space-y-0.5">
                                {taken.items.map((item) => (
                                  <li key={item.id} className="text-xs text-pvs-text-muted">
                                    {formatRange(item)} · {formatDays(item.days)}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => (editing ? setEditingUserId('') : startEditUser(user))}
                              className="flex items-center gap-1 px-3 py-2 rounded-lg border border-pvs-accent/40 text-pvs-accent text-sm font-semibold"
                            >
                              <Pencil className="w-4 h-4" /> {editing ? 'Close' : 'Edit'}
                            </button>
                            {!isSelf && (
                              <button
                                type="button"
                                onClick={() => removeUser(user)}
                                className="flex items-center gap-1 px-3 py-2 rounded-lg border border-red-400/40 text-red-300 text-sm font-semibold"
                              >
                                <Trash2 className="w-4 h-4" /> Remove
                              </button>
                            )}
                          </div>
                        </div>
                        {editing && (
                          <form onSubmit={saveEditUser} className="mt-4 pt-4 border-t border-white/10">
                            <div className="grid sm:grid-cols-2 gap-3 mb-3">
                              <label className="block">
                                <span className="text-sm text-pvs-text-muted mb-1 block">Name</span>
                                <input
                                  type="text"
                                  required
                                  minLength={2}
                                  maxLength={80}
                                  value={editFullName}
                                  onChange={(e) => setEditFullName(e.target.value)}
                                  className="pvs-input w-full"
                                />
                              </label>
                              <label className="block">
                                <span className="text-sm text-pvs-text-muted mb-1 block">Job title</span>
                                <select
                                  required
                                  value={editJobTitle}
                                  onChange={(e) => setEditJobTitle(e.target.value)}
                                  className="pvs-select w-full"
                                >
                                  <option value="">Select job title</option>
                                  {JOB_TITLES.map((title) => (
                                    <option key={title} value={title}>{title}</option>
                                  ))}
                                </select>
                              </label>
                              <label className="block">
                                <span className="text-sm text-pvs-text-muted mb-1 block">Username</span>
                                <input
                                  type="text"
                                  required
                                  minLength={3}
                                  maxLength={20}
                                  value={editUsername}
                                  onChange={(e) => setEditUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                                  className="pvs-input w-full"
                                />
                              </label>
                              <label className="block">
                                <span className="text-sm text-pvs-text-muted mb-1 block">Email</span>
                                <input
                                  type="email"
                                  value={editEmail}
                                  onChange={(e) => setEditEmail(e.target.value)}
                                  className="pvs-input w-full"
                                />
                              </label>
                              <label className="block">
                                <span className="text-sm text-pvs-text-muted mb-1 block">Account access</span>
                                <select
                                  value={editAccess}
                                  onChange={(e) => setEditAccess(e.target.value)}
                                  className="pvs-select w-full"
                                  disabled={isSelf}
                                >
                                  {ACCESS_TYPES.map((item) => (
                                    <option key={item.value} value={item.value}>{item.label}</option>
                                  ))}
                                </select>
                              </label>
                              <label className="block">
                                <span className="text-sm text-pvs-text-muted mb-1 block">Contracted hours (weekly)</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="80"
                                  step="0.5"
                                  value={editContractedHours}
                                  onChange={(e) => setEditContractedHours(e.target.value)}
                                  className="pvs-input w-full"
                                />
                              </label>
                              <label className="block">
                                <span className="text-sm text-pvs-text-muted mb-1 block">Holiday location</span>
                                <select
                                  value={editHolidayNation}
                                  onChange={(e) => setEditHolidayNation(e.target.value)}
                                  className="pvs-select w-full"
                                >
                                  {NATIONS.map((nation) => (
                                    <option key={nation} value={nation}>{NATION_LABELS[nation]}</option>
                                  ))}
                                </select>
                              </label>
                            </div>
                            <p className="text-sm text-pvs-text-muted mb-4">
                              {ACCESS_TYPES.find((item) => item.value === editAccess)?.summary}
                            </p>
                            <button type="submit" disabled={savingUser} className="pvs-btn-accent px-4 py-2 mb-4 disabled:opacity-50">
                              {savingUser ? 'Saving...' : 'Save changes'}
                            </button>
                            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
                              <label className="block">
                                <span className="text-sm text-pvs-text-muted mb-1 block">New password</span>
                                <input
                                  type="text"
                                  minLength={8}
                                  value={resetPassword}
                                  onChange={(e) => setResetPassword(e.target.value)}
                                  className="pvs-input w-full"
                                  placeholder="At least 8 characters"
                                  autoComplete="new-password"
                                />
                              </label>
                              <button
                                type="button"
                                disabled={savingUser}
                                onClick={() => resetUserPassword(user.id)}
                                className="flex items-center gap-1 px-3 py-2 rounded-lg border border-pvs-accent/40 text-pvs-accent text-sm font-semibold disabled:opacity-50"
                              >
                                <KeyRound className="w-4 h-4" /> Reset password
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </PVSPageLayout>
  );
};

export default Profile;
